import { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';
import {
    showQuantityError,
    toggleShippingNotesError,
    toggleProcessingOffer,
} from '../../actions/checkoutUIActions';
import { performApplePayAuthorization, setUpApplePay } from './helpers/applePayCheckout';
import { CheckoutServerVarsContext } from '../shared/CheckoutServerVarsContext';
import { applePayTokenSaved } from '../../actions/checkoutPaymentActions';
import { klarnaSubmitError } from '../../actions/checkoutKlarnaActions';
import { submit } from '../../actions/checkoutSubmitActions';
import { isNegotiableOrder } from '../../helpers/checkoutRedirects';
import { logCheckoutError } from '../../helpers/logCheckoutError';
import { trackCompletedStep } from '../../helpers/checkoutTracking';
import { PANES, METHODS, STEPS } from '../../constants/checkoutConstants';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    getUpcomingPane,
    isOnSidemarkPane,
    isOnReviewPane,
} from '../../reducers/checkoutPaneReducer';
import CheckoutMutationWrapper from '../../mutations/CheckoutMutationWrapper';
import { reauthorizeKlarnaPayment, finalizeKlarnaPayment } from 'dibs-klarna/exports/klarnaSdk';
import { goToOMT } from '../../helpers/checkoutRedirects';
import { useUpdateShipmentQuote } from '../../delivery/useUpdateShipmentQuote';

// components
import CheckoutNeedsToAcknowledgeRestrictions from '../../review/CheckoutNeedsToAcknowledgeRestrictions';
import CheckoutCTAButton from '../shared/CheckoutCTAButton';

const CheckoutSubmitButton = ({
    item,
    transaction,
    isReviewComplete,
    customerAcknowledgedRestrictions,
    hasQuantityError,
    transactionType,
    itemId,
    buyer,
    relay,
    viewer,
    dispatch,
    transactionState,
    buyerId,
    currencyConversionRateEventId,
    shippingAddressId,
    quantity,
    shipmentType,
    buyerChargeCurrency,
    paymentMethodApiType,
    offerCurrency,
    paymentMethodToken,
    klarnaNeedsReauthorization,
    klarnaNeedsFinalization,
    onApplePayTokenSaved,
    isComputing,
    isDisabled,
    isLoading,
    promoCode,
    shipmentNotes,
    isReturningBuyer,
    isProcessingOffer,
}) => {
    const [applePayClientInstance, setApplePayClientInstance] = useState(null);
    const [applePayPaymentRequest, setApplePayPaymentRequest] = useState(null);
    const [isProcessingApplePay, setIsProcessingApplePay] = useState(false);
    const [isProcessingKlarna, setIsProcessingKlarna] = useState(false);
    const { isMobile } = useContext(CheckoutServerVarsContext);
    const isApplePay = paymentMethodApiType === 'applePay';
    const isKlarna = paymentMethodApiType === 'klarna';
    const [updateShipmentQuote] = useUpdateShipmentQuote({ transaction });

    const getOfferAmountPerItem = () => {
        const { mostRecentOffer } = transaction;
        const convertedAmount = (mostRecentOffer?.amountPerItem?.convertedAmountList || []).find(
            a => a.currency === offerCurrency
        );
        const offerAmountPerItem = convertedAmount?.amount;
        return offerAmountPerItem;
    };

    const createOrUpdateOffer = async transactionStateRef => {
        const { billingAddress } = transactionStateRef;
        dispatch(toggleProcessingOffer(true));
        const transactionId = transaction?.serviceId;

        /**
         * Returning buyers are sent to the review step, skipping shipment step, so we need to make sure the shipment data is updated before submitting the offer.
         */
        if (isReturningBuyer) {
            await updateShipmentQuote({
                checkReturningBuyerShipment: true,
            });
        }

        try {
            await CheckoutMutationWrapper.commit(
                'TransactionCheckoutOffer',
                relay.environment,
                {
                    transactionId,
                    transactionType,
                    quantity,
                    offerAmountPerItem: getOfferAmountPerItem(),
                    offerCurrency,
                    buyerId: buyer?.serviceId,
                    itemId: transaction?.item?.serviceId,
                    skuId: transaction?.skuId,
                    paymentMethodApiType,
                    paymentMethodToken,
                    billingAddress,
                    promoCode,
                },
                isMobile
            );
        } catch (e) {
            //if user has already completed checkout, graphql will send a special error and the buyer should be redirected to the bomt
            if (e?.source?.errors?.[0]?.message === 'BUYER_ALREADY_COMPLETED_CHECKOUT') {
                goToOMT(transactionId);
                return;
            }
            logCheckoutError(e, 'offer');
        }
        dispatch(toggleProcessingOffer(false));
    };

    useEffect(() => {
        if (isApplePay) {
            setUpApplePay({
                transaction,
                viewer,
                setApplePayPaymentRequest,
                setIsProcessingApplePay,
                setApplePayClientInstance,
                dispatch,
            });
        } else if (viewer?.isMultiStageCheckoutOn) {
            createOrUpdateOffer(transactionState);
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isApplePay, transaction]);
    //For the Braintree/Apple network calls in setUpApplePay, we need the viewer object initially in order to get the client token, but we are only interested in changes to the computed transaction (specifically, changes to item quantity and charge currency). However, changing the currency and quantity triggers additional calls to the client token end point as well as to the computed transaction service, and these concurrent network calls are interfering with one another and causing relevant data to be overwritten; this results in us showing stale transaction data to the user in the Apple Pay payment panel. We can ignore changes to the viewer here because the first client token fetched works perfectly well, and avoiding additional client token fetches prevents the data overwriting problem. Same with createOrUpdateOffer function.

    const callSubmit = async modifiedTransactionState => {
        // If apple pay is enabled, we need to update the offer as it's created before apple pay is authorized
        if (modifiedTransactionState && viewer?.isMultiStageCheckoutOn) {
            await createOrUpdateOffer(modifiedTransactionState || transactionState);
        }
        //resolve transaction state and add gift status. We resolve gift status here because it is possible for the buyer to have selected gift status but for the transaction to later become ineligible, so we need to do a check before submission
        const resolvedTransactionState = Object.assign(
            {},
            modifiedTransactionState || transactionState,
            {
                isGift: transactionState.hasSelectedGift && transaction?.isGiftEligible,
            }
        );
        submit({
            relay,
            buyer,
            transactionState: resolvedTransactionState,
            viewer,
            transaction,
            dispatch,
        });
    };

    const canSubmit = ({ needsToAcknowledgeRestrictions }) => {
        const { vertical, availableQuantity, hasUnlimitedQuantity } = item;
        const { giftMessage, hasSelectedGift } = transactionState;
        const offerAmountPerItem = getOfferAmountPerItem();
        const offerQuantity = Number(quantity);

        const needsPaymentMethodToken = [
            METHODS.METHOD_CREDIT_CARD,
            METHODS.METHOD_PAYPAL_ACCOUNT,
            METHODS.METHOD_KLARNA,
        ].includes(paymentMethodApiType);

        const hasToken = !needsPaymentMethodToken || paymentMethodToken;
        const hasPaymentMethod = !!paymentMethodApiType && hasToken;
        const hasValidQuantity =
            !hasQuantityError &&
            offerQuantity > 0 &&
            (hasUnlimitedQuantity || availableQuantity >= offerQuantity);

        const hasTransactionProps = !!(
            transactionType &&
            itemId &&
            buyerId &&
            offerQuantity &&
            shipmentType &&
            shippingAddressId &&
            buyerChargeCurrency &&
            currencyConversionRateEventId &&
            (!isNegotiableOrder() || offerAmountPerItem)
        );

        const isJewelry = vertical === 'jewelry';
        const isFashion = vertical === 'fashion';
        const hasValidGiftMessage = !hasSelectedGift || isJewelry || isFashion || giftMessage;

        return (
            !isProcessingOffer &&
            !isComputing &&
            !isProcessingKlarna &&
            isReviewComplete &&
            hasPaymentMethod &&
            hasTransactionProps &&
            hasValidQuantity &&
            hasValidGiftMessage &&
            !isDisabled &&
            (!needsToAcknowledgeRestrictions || customerAcknowledgedRestrictions)
        );
    };
    async function onSubmit(event, { needsToAcknowledgeRestrictions }) {
        event.preventDefault();

        /**
         * Returning buyers are sent to the review step, skipping shipment step, so we need to make sure the shipment data is updated before submitting the offer.
         */
        if (isReturningBuyer) {
            dispatch(toggleProcessingOffer(true));
            let hasShipmentNoteError = false;
            try {
                await CheckoutMutationWrapper.commit(
                    'TransactionShipmentReview',
                    relay.environment,
                    {
                        transactionId: transaction.serviceId,
                        shipmentNotes,
                    },
                    isMobile
                );
            } catch (e) {
                hasShipmentNoteError = true;
                logCheckoutError(e, 'shipmentquote');
            }

            dispatch(toggleProcessingOffer(false));
            dispatch(toggleShippingNotesError(hasShipmentNoteError));
            if (hasShipmentNoteError) {
                return;
            }
        }

        trackCompletedStep(STEPS.STEP_REVIEW);

        if (canSubmit({ needsToAcknowledgeRestrictions })) {
            if (isApplePay) {
                performApplePayAuthorization({
                    transactionState,
                    relay,
                    buyerId,
                    applePayClientInstance,
                    setIsProcessingApplePay,
                    applePayPaymentRequest,
                    onApplePayTokenSaved,
                    callSubmit,
                    isMobile,
                    dispatch,
                });
            } else if (isKlarna) {
                if (klarnaNeedsReauthorization) {
                    setIsProcessingKlarna(true);
                    const { approved, showForm, authorizationToken } =
                        await reauthorizeKlarnaPayment();
                    setIsProcessingKlarna(false);

                    if (approved) {
                        callSubmit({
                            ...transactionState,
                            authorizationCode: authorizationToken,
                        });
                    } else if (showForm) {
                        return;
                    } else {
                        dispatch(klarnaSubmitError());
                        return;
                    }
                } else if (klarnaNeedsFinalization) {
                    setIsProcessingKlarna(true);
                    const { authorizationToken } = await finalizeKlarnaPayment();
                    setIsProcessingKlarna(false);

                    callSubmit({
                        ...transactionState,
                        authorizationCode: authorizationToken,
                    });
                } else {
                    callSubmit();
                }
            } else {
                callSubmit();
            }
        } else if (!quantity) {
            dispatch(showQuantityError());
        }
    }

    return (
        <CheckoutNeedsToAcknowledgeRestrictions {...{ transaction }}>
            {({ needsToAcknowledgeRestrictions }) => {
                const isSubmitDisabled = !canSubmit({
                    needsToAcknowledgeRestrictions,
                });
                return (
                    <CheckoutCTAButton
                        isSaving={
                            isProcessingApplePay ||
                            isProcessingOffer ||
                            isProcessingKlarna ||
                            isLoading ||
                            isComputing
                        }
                        isApplePayButton={isApplePay}
                        label={
                            isNegotiableOrder() ? (
                                <FormattedMessage
                                    id="checkout.CheckoutSubmitButton.submitOffer"
                                    defaultMessage="Submit Offer"
                                />
                            ) : (
                                <FormattedMessage
                                    id="checkout.CheckoutSubmitButton.submitOrder"
                                    defaultMessage="Submit Order"
                                />
                            )
                        }
                        dataTn={`checkout-submit-button${isSubmitDisabled ? '-disabled' : ''}`}
                        disabled={isSubmitDisabled}
                        onClick={e =>
                            onSubmit(e, {
                                needsToAcknowledgeRestrictions,
                            })
                        }
                    />
                );
            }}
        </CheckoutNeedsToAcknowledgeRestrictions>
    );
};

CheckoutSubmitButton.propTypes = {
    relay: PropTypes.object.isRequired,
    item: PropTypes.object.isRequired,
    buyer: PropTypes.any,
    transaction: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    quantity: PropTypes.number,
    isReviewComplete: PropTypes.bool.isRequired,
    customerAcknowledgedRestrictions: PropTypes.bool.isRequired,
    hasQuantityError: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
    transactionType: PropTypes.string.isRequired,
    itemId: PropTypes.string,
    buyerId: PropTypes.string,
    currencyConversionRateEventId: PropTypes.string,
    shippingAddressId: PropTypes.string,
    shipmentType: PropTypes.string,
    buyerChargeCurrency: PropTypes.string,
    paymentMethodApiType: PropTypes.string,
    offerCurrency: PropTypes.string,
    shipmentNotes: PropTypes.string,
    paymentMethodToken: PropTypes.string,
    authorizationCode: PropTypes.string,
    klarnaNeedsFinalization: PropTypes.bool,
    klarnaNeedsReauthorization: PropTypes.bool,
    transactionState: PropTypes.object.isRequired,
    isComputing: PropTypes.bool.isRequired,
    promoCode: PropTypes.string,
    onApplePayTokenSaved: PropTypes.func,
    isDisabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    isProcessingOffer: PropTypes.bool,
    isReturningBuyer: PropTypes.bool,
};

const ConnectedCheckoutSubmitButton = connect(
    ({ ui, transaction, pane, payment }) => {
        const upcomingPane = getUpcomingPane(pane);
        const isOnSidemark = isOnSidemarkPane(pane);
        const isOnReview = isOnReviewPane(pane);
        return {
            isReturningBuyer: pane.isReturningBuyer,
            shipmentNotes: transaction.shipmentNotes,
            quantity: transaction.quantity,
            transactionType: transaction.transactionType,
            itemId: transaction.itemId,
            buyerId: transaction.buyerId,
            currencyConversionRateEventId: transaction.currencyConversionRateEventId,
            shippingAddressId: transaction.shippingAddressId,
            shipmentType: transaction.shipmentType,
            buyerChargeCurrency: transaction.buyerChargeCurrency,
            paymentMethodApiType: transaction.paymentMethodApiType,
            offerCurrency: transaction.offerCurrency,
            paymentMethodToken: transaction.paymentMethodToken,
            authorizationCode: transaction.authorizationCode,
            klarnaNeedsReauthorization: payment.klarnaNeedsReauthorization,
            klarnaNeedsFinalization: payment.klarnaNeedsFinalization,
            customerAcknowledgedRestrictions: ui.customerAcknowledgedRestrictions,
            hasQuantityError: ui.hasQuantityError,
            transactionState: transaction,
            promoCode: transaction?.promoCode,
            isReviewComplete: isOnReview || (upcomingPane === PANES.PANE_REVIEW && isOnSidemark),
            isProcessingOffer: ui.isProcessingOffer,
        };
    },
    dispatch => {
        return {
            dispatch,
            ...bindActionCreators({ onApplePayTokenSaved: applePayTokenSaved }, dispatch),
        };
    }
)(CheckoutSubmitButton);

export default createFragmentContainer(ConnectedCheckoutSubmitButton, {
    viewer: graphql`
        fragment CheckoutSubmitButton_viewer on Viewer {
            ...checkoutSubmitActions_viewer
            ...applePayCheckout_viewer
            isMultiStageCheckoutOn: featureFlag(feature: "multiStageCheckout")
            isFineGrainedCheckoutErrorsEnabled: featureFlag(feature: "fineGrainedCheckoutErrors")
        }
    `,
    item: graphql`
        fragment CheckoutSubmitButton_item on Item {
            availableQuantity: quantity
            hasUnlimitedQuantity
            serviceId
            title
            vertical
            seller {
                serviceId
                sellerProfile {
                    displayName
                    email
                }
            }
        }
    `,
    buyer: graphql`
        fragment CheckoutSubmitButton_buyer on User {
            serviceId
            profile {
                displayName(preventEmailFallback: true)
                email
            }
            addressBook {
                addressList {
                    serviceId
                    zipCode
                }
            }
            wallet {
                paymentMethods {
                    paymentMethod {
                        ... on CreditCard {
                            token
                            maskedCardNumber
                        }
                        __typename
                        paymentMethodTypeDisplay
                    }
                }
            }
        }
    `,
    transaction: graphql`
        fragment CheckoutSubmitButton_transaction on Transaction {
            ...CheckoutNeedsToAcknowledgeRestrictions_transaction
            ...checkoutSubmitActions_transaction
            ...applePayCheckout_transaction
            ...useUpdateShipmentQuote_transaction
            serviceId
            skuId
            item {
                serviceId
            }
            buyerChargeCurrency
            mostRecentBuyerOffer {
                serviceId
            }
            mostRecentOffer {
                amountPerItem {
                    convertedAmountList {
                        amount
                        currency
                    }
                }
            }
            isGiftEligible
        }
    `,
});
